import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button } from 'antd';
import styled from '@emotion/styled';
import ErrorContainer from 'components/common/error-container';
import type { LoginMethod } from 'stores/mobx/auth.store';
import useStores from 'hooks/use-stores.hooks';

const CommonLoginPage: React.FC<{ loginMethod: LoginMethod }> = ({ loginMethod }) => {
  const {
    authStore: {
      show,
      logout,
      setLastLoginMethod,
      getRedirectToUrl,
      isAuthenticated,
      isCallbackPage,
      handleUniversalLoginCallback,
      hasAuth0RedirectErrorInUrl,
      handleAuth0RedirectErrorFromUrl
    }
  } = useStores();

  const [auth0RedirectErrorMessage, setAuth0RedirectErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setLastLoginMethod(loginMethod);
      if (hasAuth0RedirectErrorInUrl()) {
        setAuth0RedirectErrorMessage(handleAuth0RedirectErrorFromUrl());
        return;
      }
      if (isCallbackPage()) {
        handleUniversalLoginCallback();
      } else {
        show();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loginMethod]);

  if (!isAuthenticated && auth0RedirectErrorMessage) {
    return (
      <StyledErrorWrapper>
        <ErrorContainer message={auth0RedirectErrorMessage || ''} />
        <Button
          data-testid="back-to-login-button"
          onClick={() => {
            setAuth0RedirectErrorMessage(null);
            logout();
          }}>
          Back to login
        </Button>
      </StyledErrorWrapper>
    );
  }

  if (isAuthenticated) {
    return <Navigate replace to={getRedirectToUrl()} />;
  }

  return null;
};

const CommonLoginPageObserver = observer(CommonLoginPage);

export const SSOLoginPage = () => <CommonLoginPageObserver loginMethod="sso" />;
export const PasswordlessLoginPage = () => <CommonLoginPageObserver loginMethod="passwordless" />;

const StyledErrorWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
`;
